import { useSession } from '../lib/session'

/**
 * The mark: three columns crossed by a band the width of the page.
 *
 * It fuses the two things the product is about. The columns are the grid the
 * whole coordinate system is built on, and the band is a region drawn across
 * it. It carries no letterform, which is deliberate: the product name is
 * undecided, and a mark that spells nothing survives a name nobody has chosen
 * yet. Swapping `settings.PRODUCT_NAME` redraws nothing.
 *
 * It is a **field** rather than a figure: ink to the edge, with the band
 * knocked out. That reads as a printed page rather than as a symbol placed on
 * one. Because it bleeds, its clear space sits *outside* the tile rather than
 * inside the artwork: one column width on all four sides, and never a keyline
 * or container around it. The tile edge is already the container.
 *
 * Geometry, on a 16 x 16 unit grid:
 *   columns  3, each 4u wide, at x 0 / 6 / 12, with 2u gutters
 *   band     y 5, 6u deep, with a 1u knockout keyline
 *   bleed    full; the mark has no margin of its own
 *
 * One colour. `currentColor` by default so it takes the ink of whatever it
 * sits in and reverses correctly on the dark ground, where the band becomes
 * the ink and the columns the paper. The figure and ground swap, which is why
 * it needs no outline.
 *
 * `shapeRendering="crispEdges"` is load-bearing rather than a nicety: at 16px
 * the rectangles have to land on whole pixels or the keyline greys out.
 */
export function Logo({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      className={className}
      role="presentation"
      aria-hidden="true"
      style={{ display: 'block', flex: 'none' }}
    >
      <rect x="0" y="0" width="4" height="16" fill="currentColor" />
      <rect x="6" y="0" width="4" height="16" fill="currentColor" />
      <rect x="12" y="0" width="4" height="16" fill="currentColor" />
      {/* The knockout takes the ground, not white: on the dark theme a white
          keyline would be the brightest thing in the chrome. */}
      <rect x="1" y="5" width="14" height="6" fill="var(--color-bg)" />
      <rect x="2" y="6" width="12" height="4" fill="currentColor" />
    </svg>
  )
}

/**
 * The mark with the product name beside it. This is the only place a brand
 * appears in the chrome: one config value and one asset slot, per the brief.
 * The name is read from configuration and is never a literal in this source
 * tree, so a deployment renames the product without a code change.
 */
export function Brand({
  size = 22,
  nameClass = 'text-[17px] font-semibold tracking-[-0.005em]',
}: {
  size?: number
  nameClass?: string
}) {
  const { config } = useSession()
  return (
    <div className="flex items-center gap-2.5">
      <Logo size={size} />
      <div className={`whitespace-nowrap ${nameClass}`}>{config?.product_name ?? ''}</div>
    </div>
  )
}
